import { LandmarkIcon, SparkleIcon, SparklesIcon } from "lucide-react";
import React from "react";
import { cn } from "../../lib/cn";
import { purple, violet } from "../../lib/ui";
import type { CoachStatus } from "~/lib/coach-status";
export type { CoachStatus } from "~/lib/coach-status";

export type CoachStatusBadgeSize = "md" | "sm";

const ShieldFilled = (props: React.SVGProps<SVGSVGElement>) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="url(#shieldGradient)"
    strokeWidth={1}
    className={cn("lucide lucide-shield-icon lucide-shield", props.className)}
    style={{
      filter: "drop-shadow(0 2px 3px rgba(88, 28, 135, 0.3))",
    }}
    {...props}
  >
    <defs>
      <linearGradient id="shieldGradient" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor={purple[400]} />
        <stop offset="100%" stopColor={violet[700]} />
      </linearGradient>
    </defs>
    <path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z" />
  </svg>
);

const ANIMATION_CLASSES =
  "transition-all duration-300 east-out-expo hover:-translate-y-0.5 hover:shadow-md hover:-rotate-4";

const SIZE_VARIANTS: Record<
  CoachStatusBadgeSize,
  {
    container: string;
    icon: string;
    foundingIcon: string;
    foundingShield: string;
  }
> = {
  md: {
    container: "size-7",
    icon: "size-4",
    foundingIcon: "size-3.5",
    foundingShield: "-inset-0.5 size-8",
  },
  sm: {
    container: "size-6",
    icon: "size-3.5",
    foundingIcon: "size-3",
    foundingShield: "-inset-0.5 size-7",
  },
};

export const CoachStatusBadge: React.FC<{
  status: CoachStatus;
  className?: string;
  size?: CoachStatusBadgeSize;
}> = ({ status, className, size = "md" }) => {
  const sizeClasses = SIZE_VARIANTS[size];

  if (status === "founding") {
    return (
      <div
        className={cn(
          "relative rounded-full flex items-center justify-center",
          sizeClasses.container,
          ANIMATION_CLASSES,
          className,
        )}
      >
        <LandmarkIcon
          className={cn("z-10 text-purple-50", sizeClasses.foundingIcon)}
        />
        <ShieldFilled
          className={cn(
            "absolute z-0 stroke-purple-700",
            sizeClasses.foundingShield,
          )}
        />
      </div>
    );
  }

  if (status === "featured") {
    // return (
    //   <div
    //     className={cn(
    //       "rounded-full bg-gradient-to-br from-orange-400 to-orange-600 shadow shadow-orange-900/30 inset-ring inset-ring-orange-700/40 text-orange-50 flex items-center justify-center",
    //       sizeClasses.container,
    //       ANIMATION_CLASSES,
    //       className,
    //     )}
    //   >
    //     <StarIcon className={sizeClasses.icon} />
    //   </div>
    // );
  }

  if (status === "pro") {
    return (
      <div
        className={cn(
          "rounded-full bg-gradient-to-br from-gray-500 via-gray-400 to-gray-700 shadow shadow-gray-900/30 inset-ring inset-ring-violet-900/40 text-gray-50 flex items-center justify-center",
          sizeClasses.container,
          ANIMATION_CLASSES,
          className,
        )}
      >
        <SparkleIcon className={sizeClasses.icon} />
      </div>
    );
  }

  if (status === "proplus") {
    return (
      <div
        className={cn(
          "rounded-full bg-gradient-pro-orange shadow shadow-orange-900/30 inset-ring inset-ring-orange-700/30 text-orange-50 flex items-center justify-center",
          sizeClasses.container,
          ANIMATION_CLASSES,
          className,
        )}
      >
        <SparklesIcon className={sizeClasses.icon} />
      </div>
    );
  }

  return null;
};
